import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { getUserInfo } from 'api';
import { getInstanceSetting } from 'api/admin';
import { getUserSetting, upsertUserSetting } from 'api/user';

const FALLBACK_SENDING_DOMAIN = 'canapps.ca';

/**
 * Owns the "From" address: the editable local part (prefix), the instance-configured
 * sending domain, and the lock toggle.
 *
 * Prefix precedence: stored user setting > generated `firstname.lastname` (which is
 * then auto-seeded into the setting) > `noreply`.
 */
export interface UseFromAddressResult {
  emailFrom: string;
  emailPrefix: string;
  /** Sets the prefix and keeps `emailFrom` in sync. Strips illegal characters. */
  changePrefix: (value: string) => void;
  /**
   * Set the prefix without touching `emailFrom` — the prefix/domain effect will
   * recombine it. Used by draft restore, which may run before the domain has loaded.
   */
  setEmailPrefix: (value: string) => void;
  domain: string;
  isPrefixLocked: boolean;
  togglePrefixLock: () => void;
  /** Persist the prefix if it actually changed. Wire to the field's `onBlur`. */
  savePrefix: () => Promise<void>;
  /** Used by edit mode, which loads From from the scheduled email itself. */
  applyAddress: (fullAddress: string) => void;
  reset: () => void;
}

export function useFromAddress(): UseFromAddressResult {
  const { userInfo } = useSelector(selectUserInfo);

  const [emailFrom, setEmailFrom] = useState('');
  const [emailPrefix, setEmailPrefix] = useState('');
  const [domain, setDomain] = useState('');
  const [isPrefixLocked, setIsPrefixLocked] = useState(true);

  // Last value persisted to the canmail/email_prefix user setting, so the blur handler
  // can skip no-op PUTs.
  const lastSavedPrefixRef = useRef<string | null>(null);

  useEffect(() => {
    async function fetchFromFieldData() {
      const currentUserId = userInfo?.userId;
      const prefixLookup = currentUserId
        ? getUserSetting(currentUserId, 'canmail', 'email_prefix')
        : Promise.resolve(null);

      const [userInfoResult, domainResult, prefixResult] = await Promise.allSettled([
        getUserInfo(),
        getInstanceSetting('canmail', 'sending_domain'),
        prefixLookup,
      ]);

      const storedPrefix =
        prefixResult.status === 'fulfilled' &&
        typeof prefixResult.value === 'string' &&
        prefixResult.value.trim()
          ? prefixResult.value.trim()
          : null;

      if (storedPrefix) {
        setEmailPrefix(storedPrefix);
        lastSavedPrefixRef.current = storedPrefix;
      } else if (userInfoResult.status === 'fulfilled') {
        const userInfoResponse: any = userInfoResult.value;
        const generated = `${userInfoResponse.data.FirstName.toLowerCase()}.${userInfoResponse.data.LastName.toLowerCase()}`;
        setEmailPrefix(generated);
        // Only auto-seed when the setting row is genuinely missing (fulfilled with
        // null) AND we have a userId. A rejected fetch could be a transient error —
        // don't create a row in that case.
        if (prefixResult.status === 'fulfilled' && currentUserId) {
          try {
            await upsertUserSetting(currentUserId, 'canmail', 'email_prefix', generated);
            lastSavedPrefixRef.current = generated;
          } catch (error) {
            console.error('[useFromAddress] Failed to auto-seed email_prefix setting:', error);
          }
        } else if (prefixResult.status === 'rejected') {
          console.error('[useFromAddress] Failed to fetch email_prefix setting:', prefixResult.reason);
        }
      } else {
        console.error('[useFromAddress] Failed to fetch user info:', userInfoResult.reason);
        if (prefixResult.status === 'rejected') {
          console.error('[useFromAddress] Failed to fetch email_prefix setting:', prefixResult.reason);
        }
        setEmailPrefix('noreply');
      }

      if (domainResult.status === 'fulfilled') {
        const value = domainResult.value as string;
        setDomain(value && value.trim() ? value.trim() : FALLBACK_SENDING_DOMAIN);
      } else {
        console.error('[useFromAddress] Failed to fetch sending_domain setting:', domainResult.reason);
        setDomain(FALLBACK_SENDING_DOMAIN);
      }
    }

    fetchFromFieldData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Combine prefix and domain into the complete address on initial load.
  useEffect(() => {
    if (emailPrefix && domain) {
      setEmailFrom(`${emailPrefix}@${domain}`);
    }
  }, [emailPrefix, domain]);

  const changePrefix = useCallback(
    (value: string) => {
      const cleaned = value.replace(/[^a-zA-Z0-9._-]/g, '');
      setEmailPrefix(cleaned);
      setEmailFrom(`${cleaned}@${domain}`);
    },
    [domain]
  );

  const savePrefix = useCallback(async () => {
    const trimmed = emailPrefix.trim();
    const currentUserId = userInfo?.userId;
    if (!trimmed || !currentUserId || trimmed === lastSavedPrefixRef.current) return;
    try {
      await upsertUserSetting(currentUserId, 'canmail', 'email_prefix', trimmed);
      lastSavedPrefixRef.current = trimmed;
    } catch (error) {
      console.error('[useFromAddress] Failed to save email_prefix setting:', error);
    }
  }, [emailPrefix, userInfo?.userId]);

  const togglePrefixLock = useCallback(() => {
    setIsPrefixLocked((prev) => !prev);
  }, []);

  /**
   * Adopt an address that came from elsewhere (a scheduled email being edited).
   * Splits into prefix/domain when possible and unlocks the field for editing.
   */
  const applyAddress = useCallback((fullAddress: string) => {
    const parts = fullAddress.split('@');
    if (parts.length === 2) {
      setEmailPrefix(parts[0]);
      setDomain(parts[1]);
      setEmailFrom(fullAddress);
      setIsPrefixLocked(false);
    } else {
      // Shouldn't happen with valid data — fall back to setting the full address.
      setEmailFrom(fullAddress);
    }
  }, []);

  const reset = useCallback(() => {
    setIsPrefixLocked(true);
  }, []);

  // NOTE: the raw `setEmailFrom` and `setIsPrefixLocked` setters are deliberately not
  // exposed — writing either directly bypasses the prefix/domain sync. Use
  // `changePrefix`, `applyAddress` or `togglePrefixLock` instead.
  return {
    emailFrom,
    emailPrefix,
    changePrefix,
    setEmailPrefix,
    domain,
    isPrefixLocked,
    togglePrefixLock,
    savePrefix,
    applyAddress,
    reset,
  };
}

export default useFromAddress;
